/**
 * The bands the HSK exam syllabus grades its vocabulary into.
 *
 * The syllabus keeps the nine levels of the 2021 standard,
 * 国际中文教育中文水平等级标准, and publishes one word list for the top three
 * of them. The list therefore has seven bands.
 *
 * The terms here are the keys of `articles/data/HSK.yaml`, spelled the same
 * way, so that a band read off the list is already the term an article's front
 * matter carries and the term Hugo urlizes to `/hsk/hsk-3/`. `docs/dengji.md`
 * has the reasoning.
 */

/**
 * The front matter key holding an HSK band, which is the taxonomy's own name.
 *
 * `articles/config.yaml` declares the taxonomy as `HSK`, and a page carries its
 * term under that key the way a grammar article does. The address Hugo urlizes
 * it to is `/hsk/hsk-3/`. `docs/dengji.md` has why the terms are whole names.
 */
export const hskKey = "HSK";

/** One band of the word list, as `articles/data/HSK.yaml` names it. */
export type HskBand =
  | "HSK 1"
  | "HSK 2"
  | "HSK 3"
  | "HSK 4"
  | "HSK 5"
  | "HSK 6"
  | "HSK 7-9";

/**
 * The seven bands in order, from the first 500 words to the advanced list.
 *
 * The order is the ranking. A word the list grades twice belongs at the first
 * band a learner meets it in, and `HskWordList` settles that by position here.
 */
export const hskBands: readonly HskBand[] = [
  "HSK 1",
  "HSK 2",
  "HSK 3",
  "HSK 4",
  "HSK 5",
  "HSK 6",
  "HSK 7-9",
];

/** What every band's term begins with, which its `Level` column leaves off. */
const bandPrefix = "HSK ";

/**
 * The band a `Level` value names, or nothing where the value names none.
 *
 * The column holds `1` to `6` and `7-9`, which are the taxonomy's terms with
 * the `HSK ` prefix taken off. Anything else is a column this reader has no
 * business guessing at, and it comes back as nothing so that a row carrying one
 * is dropped instead of being filed under a band it never claimed.
 */
export function hskBandOf(level: string): HskBand | undefined {
  return hskBandNamed(`${bandPrefix}${level.trim()}`);
}

/**
 * The `Level` column a band would be written in, `3` or `7-9`.
 *
 * The other direction from {@link hskBandOf}, for a caller writing a band into
 * a file that holds thousands of them. The 词典 address index publishes the
 * column rather than the term, which saves 26 KB over the section.
 */
export function hskBandLevel(band: HskBand): string {
  return band.slice(bandPrefix.length);
}

/**
 * The band a whole term names, or nothing where the term names none.
 *
 * For a caller holding the term rather than the column: a page's front matter
 * carries `HSK 3`, where the file's `Level` column carries `3`. Anything else
 * comes back as nothing, so a page carrying a band this scale has never had is
 * read as carrying none rather than as carrying that.
 */
export function hskBandNamed(term: string): HskBand | undefined {
  return hskBands.find((band) => band === term.trim());
}

/** How far into the list a band sits, counting from zero. */
export function hskBandRank(band: HskBand): number {
  return hskBands.indexOf(band);
}
